import { useEffect, useState } from "react";
import { FolderSync, Phone, MessageCircle, IndianRupee, Landmark, CheckCircle2, ClipboardList, RefreshCw, MessagesSquare, Copy, Save as SaveIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";

/**
 * MovingFilesView — joint tracking page for the telecaller after a bank staff
 * has TAKEN one of their pushed leads. Read-only for the telecaller:
 * shows who took it, when, live approval status (CIBIL / Legal / Tech), and
 * quick contact actions (call customer, WhatsApp customer, call bank staff).
 */
function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ago`;
  if (h > 0) return `${h}h ${m}m ago`;
  if (m > 0) return `${m}m ago`;
  return "just now";
}

// -------------------------------------------------------------------------
// WhatsApp Group section — TELECALLER is the creator/editor.
// Shows Create button (when no group), invite input + Save, Join/Copy once set.
// -------------------------------------------------------------------------
function WAGroupSection({ c, onChange }) {
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(c.wa_group_invite_link || "");
  const hasLink = !!c.wa_group_invite_link;
  useEffect(() => { setDraft(c.wa_group_invite_link || ""); }, [c.wa_group_invite_link]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post(`/telecaller/case/${c.lead_id}/create-wa-group`);
      try { await navigator.clipboard.writeText(r.data.seed_message); } catch {}
      window.open(r.data.seed_wa_url, "_blank", "noopener,noreferrer");
      toast.success(`Seed message copied · Group: ${r.data.group_name}`);
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setCreating(false); }
  };

  const save = async () => {
    const link = draft.trim();
    if (link && !/^https:\/\/(chat\.whatsapp\.com|wa\.me)\//.test(link)) {
      return toast.error("Link must start with https://chat.whatsapp.com/ or https://wa.me/");
    }
    setSaving(true);
    try {
      await api.put(`/telecaller/case/${c.lead_id}/wa-group`, { wa_group_invite_link: link, wa_group_name: c.wa_group_name || "" });
      toast.success(link ? "Invite link saved · Bank staff + customer can now join" : "Invite link cleared");
      onChange && onChange();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  const copy = async () => {
    if (!c.wa_group_invite_link) return;
    try { await navigator.clipboard.writeText(c.wa_group_invite_link); toast.success("Invite link copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mt-3 rounded-xl bg-[#0F1424] border border-[#25D366]/25 p-3" data-testid={`wa-group-${c.lead_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
          <MessagesSquare className="w-4 h-4 text-[#25D366]"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#25D366] font-bold">WhatsApp Group</div>
          <div className="text-[10px] text-slate-500">Shared thread · telecaller + bank staff + admin + customer</div>
        </div>
        <div className="text-[9px] text-slate-500">
          {hasLink ? <span className="text-emerald-400 font-bold">Created {timeAgo(c.wa_group_created_at)}</span> : <span className="text-orange-400 font-bold">Not created yet</span>}
        </div>
      </div>
      {c.wa_group_name && (
        <div className="text-[10px] text-slate-300 font-mono-alt border border-slate-800 bg-[#0B1020] rounded-md px-2 py-1 mb-2 break-all">
          {c.wa_group_name}
        </div>
      )}
      {!hasLink && (
        <button
          type="button"
          onClick={create}
          disabled={creating}
          data-testid={`wa-create-${c.lead_id}`}
          className="w-full h-10 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-xs flex items-center justify-center gap-2 mb-2">
          <MessagesSquare className="w-4 h-4"/>{creating ? "Preparing…" : "Create WA Group"}
        </button>
      )}
      <div className="space-y-1.5">
        <label className="text-[9px] uppercase tracking-widest text-slate-500">Group invite link</label>
        <div className="flex gap-1.5">
          <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="https://chat.whatsapp.com/…"
            data-testid={`wa-input-${c.lead_id}`}
            className="bg-[#0B1020] border-slate-700 text-white h-9 text-xs font-mono-alt"
          />
          <button
            type="button"
            onClick={save}
            disabled={saving || draft === (c.wa_group_invite_link || "")}
            data-testid={`wa-save-${c.lead_id}`}
            className="shrink-0 px-3 h-9 rounded-md bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold disabled:opacity-40 flex items-center gap-1">
            <SaveIcon className="w-3 h-3"/>Save
          </button>
        </div>
      </div>
      {hasLink && (
        <div className="grid grid-cols-2 gap-1.5 mt-2">
          <a
            href={c.wa_group_invite_link}
            target="_blank" rel="noreferrer"
            data-testid={`join-wa-group-${c.lead_id}`}
            className="h-9 rounded-md bg-[#25D366] hover:bg-[#20bd5a] text-white text-xs font-bold flex items-center justify-center gap-1">
            <MessagesSquare className="w-3 h-3"/>Join Group
          </a>
          <button
            type="button"
            onClick={copy}
            data-testid={`wa-copy-${c.lead_id}`}
            className="h-9 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-xs font-bold flex items-center justify-center gap-1">
            <Copy className="w-3 h-3"/>Copy
          </button>
        </div>
      )}
    </div>
  );
}

function ProgressTracker({ step }) {
  const steps = [
    { i: 1, label: "Assigned",   icon: ClipboardList },
    { i: 2, label: "Bank Pool",  icon: Landmark },
    { i: 3, label: "Moving",     icon: FolderSync },
    { i: 4, label: "Disbursed",  icon: CheckCircle2 },
  ];
  return (
    <div className="flex items-center gap-1 mt-3" data-testid="progress-tracker">
      {steps.map((s, idx) => {
        const done = s.i < step;
        const active = s.i === step;
        return (
          <div key={s.i} className="flex items-center flex-1">
            <div className={`flex flex-col items-center gap-0.5 flex-1 ${active ? "text-orange-400" : done ? "text-emerald-400" : "text-slate-600"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border ${active ? "bg-orange-500/15 border-orange-500 animate-pulse" : done ? "bg-emerald-500/15 border-emerald-500" : "bg-slate-800 border-slate-700"}`}>
                <s.icon className="w-3.5 h-3.5"/>
              </div>
              <span className={`text-[9px] uppercase tracking-widest font-bold ${active ? "" : "opacity-70"}`}>{s.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mx-0.5 ${s.i < step ? "bg-emerald-500/60" : "bg-slate-800"}`}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ApprovalPill({ label, status }) {
  const cls = status === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
              status === "rejected" ? "bg-red-500/15 text-red-300 border-red-500/30" :
              "bg-slate-800 text-slate-400 border-slate-700";
  const txt = status === "approved" ? "✓" : status === "rejected" ? "✗" : "…";
  return (
    <div className={`flex-1 rounded-md border ${cls} px-2 py-1.5 flex items-center justify-between text-[10px] uppercase tracking-widest font-bold`}>
      <span>{label}</span><span>{txt}</span>
    </div>
  );
}

function MovingCard({ c }) {
  const initials = (c.bank_staff_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const waLink = c.phone ? `https://wa.me/91${c.phone}?text=${encodeURIComponent(`Hi ${c.name || "there"}, this is Sakthi Loan Consultancy — a bank staff is now processing your loan file. We'll keep you updated.`)}` : null;
  return (
    <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-4" data-testid={`moving-${c.lead_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-mono-alt text-[#C5A028] text-xs">{c.lead_id}</div>
        <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/40 text-[10px] uppercase tracking-widest animate-pulse">
          <FolderSync className="w-2.5 h-2.5 mr-1"/> Moving
        </Badge>
      </div>
      <div className="font-heading text-xl text-white leading-tight break-words">{c.name}</div>
      <div className="mt-1 space-y-0.5 text-sm">
        {c.phone && (
          <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-emerald-300 hover:underline">
            <Phone className="w-3.5 h-3.5"/><span className="font-mono-alt">{c.phone}</span>
          </a>
        )}
        <div className="text-white flex items-center gap-1.5">
          <IndianRupee className="w-3.5 h-3.5"/>{parseFloat(c.amount || 0).toLocaleString("en-IN")}
          <span className="text-slate-400 uppercase text-xs tracking-wider">· {c.loan_type}</span>
        </div>
      </div>

      {/* Bank staff card */}
      <div className="mt-3 rounded-xl bg-[#0F1424] border border-[#1E2742] p-3 flex items-center gap-3" data-testid={`bank-staff-${c.lead_id}`}>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C5A028] to-[#8a6f1c] flex items-center justify-center text-slate-900 font-bold text-xs shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Handled by</div>
          <div className="text-white font-semibold truncate">{c.bank_staff_name || "—"}
            {c.bank_staff_bank ? <span className="text-slate-400 text-xs font-normal ml-1">· {c.bank_staff_bank}</span> : null}
          </div>
          <div className="text-[11px] text-slate-500">{c.bank_staff_district || "—"} · Taken {timeAgo(c.taken_at)}</div>
        </div>
        {c.bank_staff_phone && (
          <a
            href={`tel:${c.bank_staff_phone}`}
            data-testid={`call-bank-staff-${c.lead_id}`}
            className="shrink-0 w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 flex items-center justify-center text-emerald-300 transition"
            title={`Call ${c.bank_staff_name || "bank staff"}`}
          ><Phone className="w-4 h-4"/></a>
        )}
      </div>

      {/* Progress tracker */}
      <ProgressTracker step={c.progress_step || 3}/>

      {/* Approvals — read-only */}
      <div className="mt-3 rounded-lg bg-[#0F1424] border border-[#1E2742] p-2.5">
        <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">Bank Staff Approvals · Read-only</div>
        <div className="flex gap-1.5">
          <ApprovalPill label="CIBIL" status={c.cibil_status}/>
          <ApprovalPill label="Legal" status={c.legal_status}/>
          <ApprovalPill label="Tech"  status={c.tech_status}/>
        </div>
      </div>

      {/* Contact actions */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        {c.phone && (
          <a href={`tel:${c.phone}`} data-testid={`call-customer-${c.lead_id}`}
             className="h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold flex items-center justify-center gap-2 text-sm">
            <Phone className="w-4 h-4"/> Call Customer
          </a>
        )}
        {waLink && (
          <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-customer-${c.lead_id}`}
             className="h-11 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold flex items-center justify-center gap-2 text-sm">
            <MessageCircle className="w-4 h-4"/> WhatsApp
          </a>
        )}
      </div>
      {c.wa_group_invite_link && (
        <a
          href={c.wa_group_invite_link}
          target="_blank" rel="noopener noreferrer"
          data-testid={`join-wa-group-inline-${c.lead_id}`}
          className="mt-2 h-11 w-full rounded-full bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/40 text-[#25D366] font-bold flex items-center justify-center gap-2 text-sm"
        >
          <MessagesSquare className="w-4 h-4"/> Join Case Group
        </a>
      )}
      <WAGroupSection c={c} onChange={() => window.dispatchEvent(new Event("moving-files-refresh"))}/>
    </div>
  );
}

export default function MovingFilesView() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/staff/moving-files")
      .then(r => setCases(r.data.cases || []))
      .catch(e => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onRefresh = () => load();
    window.addEventListener("moving-files-refresh", onRefresh);
    return () => { clearInterval(t); window.removeEventListener("moving-files-refresh", onRefresh); };
  }, []);

  return (
    <div className="space-y-3 pb-24" data-testid="moving-files-view">
      <div className="rounded-2xl bg-gradient-to-br from-orange-500/10 to-slate-900 border border-orange-500/25 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-orange-500/20 border border-orange-500/40 flex items-center justify-center shrink-0">
          <FolderSync className="w-5 h-5 text-orange-400"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Moving Files</div>
          <div className="text-white font-heading text-lg leading-tight">
            {cases.length} case{cases.length === 1 ? "" : "s"} with bank staff
          </div>
          <div className="text-[11px] text-slate-500">Cases you pushed that a bank staff has picked up but not yet disbursed.</div>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="moving-refresh"
          className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400 shrink-0"
          title="Refresh"
        ><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/></button>
      </div>

      {loading && cases.length === 0 ? (
        <div className="text-center text-slate-500 py-8" data-testid="moving-loading">Loading…</div>
      ) : cases.length === 0 ? (
        <div className="rounded-2xl bg-[#141A2E] border border-[#1E2742] p-8 text-center" data-testid="moving-empty">
          <div className="mx-auto w-16 h-16 rounded-full bg-slate-800/60 flex items-center justify-center mb-3">
            <FolderSync className="w-8 h-8 text-slate-500"/>
          </div>
          <div className="text-white font-bold text-lg">No files moving</div>
          <div className="text-slate-500 text-sm mt-1 leading-relaxed">
            When a bank staff takes one of your cases from the pool, it will show up here for joint tracking until disbursed or returned.
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="moving-grid">
          {cases.map(c => <MovingCard key={c.lead_id} c={c}/>)}
        </div>
      )}
    </div>
  );
}
